import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import {
  GoogleMap,
  MarkerF,
  Autocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";

const mapContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "8px",
};

const defaultCenter = { lat: 51.5074, lng: -0.1278 }; // London

const AddressAddModal = ({
  showModal,
  handleClose,
  changeAddress,
  initialAddress = null,
}) => {
  const [address, setAddress] = useState("");
  const [marker, setMarker] = useState(defaultCenter);
  const [error, setError] = useState("");
  const [isBouncing, setIsBouncing] = useState(false);

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  // Load initial address when modal opens (for edit mode)
  useEffect(() => {
    if (showModal && initialAddress?.address) {
      setAddress(initialAddress.address);

      // Clear previous marker state — will be set after geocoding
      setMarker(defaultCenter);

      // If map is already loaded, geocode now
      if (mapRef.current && window.google?.maps?.Geocoder) {
        geocodeAddress(initialAddress.address);
      }
      // If map not loaded yet, geocoding will happen in onMapLoad
    } else {
      // Reset for new address
      setAddress("");
      setMarker(defaultCenter);
    }
  }, [showModal, initialAddress]);

  // Helper: Geocode address and update map/marker
  const geocodeAddress = (addr) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: addr }, (results, status) => {
      if (status === "OK" && results[0]) {
        const { lat, lng } = results[0].geometry.location;
        const position = { lat: lat(), lng: lng() };

        // Update state
        setMarker(position);
        // Move map
        mapRef.current?.panTo(position);
        mapRef.current?.setZoom(17);
      } else {
        console.warn("Geocoding failed for:", addr, status);
        // Optional: fallback to default
        setMarker(defaultCenter);
        mapRef.current?.panTo(defaultCenter);
        mapRef.current?.setZoom(15);
      }
    });
  };

  // Update onMapLoad to handle initial geocoding if needed
  const onMapLoad = (map) => {
    mapRef.current = map;

    // If modal is open and we have an initial address but no coords yet → geocode now
    if (
      showModal &&
      initialAddress?.address &&
      address === initialAddress.address
    ) {
      // Small delay ensures map is fully interactive (optional but safe)
      setTimeout(() => {
        geocodeAddress(initialAddress.address);
      }, 100);
    }
  };

  /* ---------------- AUTOCOMPLETE ---------------- */
  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();

    if (!place || !place.geometry) {
      setError("Please select an address from the list");
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    setAddress(place.formatted_address);
    setMarker({ lat, lng });

    // Animate marker briefly
    setIsBouncing(true);
    setTimeout(() => setIsBouncing(false), 1400);

    mapRef.current.panTo({ lat, lng });
    mapRef.current.setZoom(17);
  };

  /* ---------------- MAP CLICK ---------------- */
  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setMarker({ lat, lng });

    // Animate marker briefly
    setIsBouncing(true);
    setTimeout(() => setIsBouncing(false), 1400);

    mapRef.current.panTo({ lat, lng });
    mapRef.current.setZoom(17);

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        setAddress(results[0].formatted_address);
      }
    });
  };

  /* ---------------- MARKER DRAG ---------------- */
  const handleMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    setMarker({ lat, lng });

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        setAddress(results[0].formatted_address);
      }
    });
  };

  /* ---------------- SAVE ---------------- */
  const saveAddress = () => {
    if (!address) {
      setError("Please select a valid address");
      return;
    }

    // Just return the address data without API call
    changeAddress({
      address,
      latitude: marker.lat,
      longitude: marker.lng,
    });

    // Reset modal
    setAddress("");
    setMarker(defaultCenter);
    handleClose();
  };

  // Reset state when modal closes
  const handleModalClose = () => {
    setAddress("");
    setMarker(defaultCenter);
    setError("");
    handleClose();
  };

  return (
    <Modal
      show={showModal}
      onHide={handleModalClose}
      size="lg"
      backdropClassName="address-modal-backdrop"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {initialAddress ? "Edit Address" : "Select Address"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!isLoaded && <p>Loading map...</p>}

        {isLoaded && (
          <>
            <Form.Label>Search UK Address</Form.Label>
            <Autocomplete
              onLoad={(auto) => (autocompleteRef.current = auto)}
              onPlaceChanged={onPlaceChanged}
              options={{
                componentRestrictions: { country: "gb" },
                fields: ["geometry", "formatted_address"],
              }}
            >
              <Form.Control
                placeholder="Type UK address..."
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                autoComplete="off"
              />
            </Autocomplete>

            <div className="mt-3">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={marker}
                zoom={15}
                onLoad={onMapLoad}
                onClick={handleMapClick}
              >
                <MarkerF
                  position={marker}
                  draggable
                  animation={
                    isBouncing ? window.google.maps.Animation.BOUNCE : undefined
                  }
                  onDragEnd={handleMarkerDragEnd}
                />
              </GoogleMap>
            </div>
          </>
        )}

        {error && <div className="text-danger mt-2">{error}</div>}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={handleModalClose}>
          Cancel
        </Button>
        <Button onClick={saveAddress} disabled={!isLoaded}>
          Use This Address
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddressAddModal;
